// Services with stub fallbacks (decision 0033: "services with stub fallbacks"; suggestion "Service registry with
// useful stubs and stub fallback"). A service is named by a typed key that carries its stub. The host wires real
// providers into createGame; a module asks for a service by its key and always gets something that works: the
// provider when the host gave one, else the stub. A provider factory that throws falls back to the stub and logs
// once. `Game.services()` reports which ran for real, so a test (or an agent) can see what was stubbed.

/** A typed service key: a well-known name plus the stub used when no provider is wired. */
export interface ServiceKey<T> {
  readonly key: string;
  /** Build the stand-in. Should be useful (a fixed answer, a no-op sink), not just a throw. */
  readonly stub: () => T;
}

/** Define a service key. `stub` builds the stand-in used when the host wires no provider. */
export function defineService<T>(key: string, stub: () => T): ServiceKey<T> {
  return { key, stub };
}

/** How a service was resolved: the host's provider, the stub (no provider), or the stub after the provider threw. */
export type ServiceStatus = "provided" | "stub" | "stub-fallback";

/** One resolved service, as `Game.services()` lists it. */
export interface ServiceInfo {
  key: string;
  status: ServiceStatus;
  /** The provider's error, for `stub-fallback`. */
  error?: string;
}

const LAZY = Symbol("rpg.lazyProvider");

/** A provider factory the kernel runs on first use; see {@link lazyProvider}. */
export interface LazyProvider<T> {
  readonly [LAZY]: () => T;
}

/** Wrap a factory so the kernel runs it on first use, and falls back to the stub if it throws. */
export function lazyProvider<T>(create: () => T): LazyProvider<T> {
  return { [LAZY]: create };
}

function isLazy(p: unknown): p is LazyProvider<unknown> {
  return typeof p === "object" && p !== null && LAZY in p;
}

/** The kernel's service table: resolves each key once, remembers how. Internal to createGame. */
export class ServiceTable {
  private readonly resolved = new Map<string, { value: unknown; info: ServiceInfo }>();

  constructor(
    private readonly providers: Readonly<Record<string, unknown>>,
    private readonly log: (message: string) => void,
  ) {}

  /** The provider for `key`, or its stub. Resolved once per game. */
  get<T>(key: ServiceKey<T>): T {
    const hit = this.resolved.get(key.key);
    if (hit) return hit.value as T;
    let value: unknown;
    let info: ServiceInfo;
    const p = this.providers[key.key];
    if (p === undefined || p === null) {
      value = key.stub();
      info = { key: key.key, status: "stub" };
    } else if (isLazy(p)) {
      try {
        value = p[LAZY]();
        info = { key: key.key, status: "provided" };
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        this.log(`[kernel] service "${key.key}": provider threw (${error}); using its stub`);
        value = key.stub();
        info = { key: key.key, status: "stub-fallback", error };
      }
    } else {
      value = p;
      info = { key: key.key, status: "provided" };
    }
    this.resolved.set(key.key, { value, info });
    return value as T;
  }

  /** Every service resolved so far, in first-use order. */
  list(): ServiceInfo[] {
    return [...this.resolved.values()].map((r) => ({ ...r.info }));
  }
}
